import React, { useState } from "react";
import { Flex, Checkbox } from "@chakra-ui/react";
import Fieldset from "../../../general/form/Fieldset";
import GicsSelect from "./fields/GicsSelect";
import CountrySelect from "./fields/CountrySelect";
import CustomReactSelect from "../../../general/form/CustomReactSelect";

const continentalOptions = [
  { value: "002", label: "Africa (002)" },
  { value: "019", label: "Americas (019)" },
  { value: "142", label: "Asia (142)" },
  { value: "150", label: "Europe (150)" },
  { value: "009", label: "Oceania (009)" },
];

const regionalOptionsGrouped = [
  {
    label: "Africa (002)",
    options: [
      { value: "015", label: "North Africa (015)" },
      { value: "202", label: "Sub-Saharan Africa (202)" },
      { value: "017", label: "Central Africa (017)" },
      { value: "014", label: "East Africa (014)" },
      { value: "018", label: "Southern Africa (018)" },
      { value: "011", label: "West Africa (011)" },
    ],
  },
  {
    label: "Americas (019)",
    options: [
      { value: "021", label: "North America (021)" },
      { value: "419", label: "Latin America and Caribbean (419)" },
      { value: "013", label: "Central America (013)" },
      { value: "005", label: "South America (005)" },
      { value: "029", label: "Caribbean (029)" },
    ],
  },
  {
    label: "Asia (142)",
    options: [
      { value: "143", label: "Central Asia (143)" },
      { value: "030", label: "East Asia (030)" },
      { value: "035", label: "Southeast Asia (035)" },
      { value: "034", label: "South Asia (034)" },
      { value: "145", label: "West Asia (145)" },
    ],
  },
  {
    label: "Europe (150)",
    options: [
      { value: "151", label: "Eastern Europe (151)" },
      { value: "154", label: "Northern Europe (154)" },
      { value: "039", label: "Southern Europe (039)" },
      { value: "155", label: "Western Europe (155)" },
    ],
  },
  {
    label: "Oceania (009)",
    options: [
      { value: "053", label: "Australia and New Zealand (053)" },
      { value: "054", label: "Melanesia (054)" },
      { value: "057", label: "Micronesia (057)" },
      { value: "061", label: "Polynesia (061)" },
    ],
  },
];

const OnboardingStep22 = ({ formData, handleChange }) => {
  const [selectedGeographicFocus, setSelectedGeographicFocus] = useState([]);
  const [selectedContinental, setSelectedContinental] = useState([]);
  const [selectedRegional, setSelectedRegional] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  const handleGicsChange = (selectedOptions) => {
    handleChange({
      target: {
        name: "gicsSectors",
        value: selectedOptions
          ? selectedOptions.map((option) => option.value)
          : [],
      },
    });
  };

  const handleGeographicFocusChange = (option) => {
    let newFocus = [...selectedGeographicFocus];
  
    if (newFocus.includes(option)) {
      // If the option is already selected, remove it
      newFocus = newFocus.filter((focus) => focus !== option);
    } else {
      // Add the option
      newFocus.push(option);
    }
  
    if (option === "Global" && !selectedGeographicFocus.includes(option)) {
      // If selecting "Global," clear all other selections
      newFocus = ["Global"];
    } else if (option !== "Global") {
      // If selecting anything else, remove "Global"
      newFocus = newFocus.filter((focus) => focus !== "Global");
    }
  
    // Mutually exclusive logic for Continental and Regional
    if (option === "Continental" && !newFocus.includes("Regional")) {
      newFocus = newFocus.filter((focus) => focus !== "Regional");
    } else if (option === "Regional" && !newFocus.includes("Continental")) {
      newFocus = newFocus.filter((focus) => focus !== "Continental");
    }
  
    setSelectedGeographicFocus(newFocus);
    handleChange({ target: { name: "geographicFocus", value: newFocus } });
  };
  

  const handleContinentalChange = (selectedOptions) => {
    setSelectedContinental(selectedOptions);
    handleChange({
      target: {
        name: "selectedContinental",
        value: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
      },
    });
  };

  const handleRegionalChange = (selectedOptions) => {
    setSelectedRegional(selectedOptions);
    handleChange({
      target: {
        name: "selectedRegional",
        value: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
      },
    });
  };

  const handleCountrySpecificChange = (selectedOptions) => {
    setSelectedCountries(selectedOptions);
    handleChange({
      target: {
        name: "selectedCountries",
        value: selectedOptions
          ? selectedOptions.map((option) => option.value)
          : [],
      },
    });
  };

  return (
    <>
      <Flex direction="column" gap={8}>
        <h6>Investment Focus</h6>

        <Flex direction="column">
          <Fieldset
            flex={1}
            label="Target sectors"
            description="Select relevant GICS sectors for the fund. Multiple selections allowed."
            input={<GicsSelect onChange={handleGicsChange} />}
          />

          <Fieldset
            label="Geographic focus"
            description="Select geographic focus level(s) for the fund. Multiple selections allowed."
            flex={1}
            input={
              <Flex direction={"column"} gap={4}>
                <Flex gap={8} rowGap={4} flex={1} flexWrap={"wrap"}>
                  {["Global", "Continental", "Regional", "Country-specific"].map(
                    (option) => (
                      <Checkbox
                        key={option}
                        isChecked={selectedGeographicFocus.includes(option)}
                        isDisabled={
                          selectedGeographicFocus.includes("Global") &&
                          option !== "Global"
                        }
                        onChange={() =>
                          handleGeographicFocusChange(option)
                        }
                      >
                        {option}
                      </Checkbox>
                    )
                  )}
                </Flex>

                {/* Continental Select */}
                {selectedGeographicFocus.includes("Continental") && (
                  <CustomReactSelect
                    isMulti
                    isSearchable
                    isClearable
                    placeholder="Select continent(s)"
                    value={selectedContinental}
                    options={continentalOptions}
                    onChange={handleContinentalChange}
                  />
                )}

                {/* Regional Select */}
                {selectedGeographicFocus.includes("Regional") && (
                  <CustomReactSelect              
                    isMulti
                    isSearchable
                    isClearable
                    placeholder="Select region(s)"
                    value={selectedRegional}
                    options={regionalOptionsGrouped}
                    onChange={handleRegionalChange}
                  />
                )}

                {/* Country-specific dropdown */}
                {selectedGeographicFocus.includes("Country-specific") && (
                  <CountrySelect
                    placeholder="Select countries"
                    value={selectedCountries}
                    onChange={handleCountrySpecificChange}
                  />
                )}
              </Flex>
            }
          />
        </Flex>
      </Flex>
    </>
  );
};

export default OnboardingStep22;
